<template>
  <v-flex class="pl-2 pr-2">
    <v-layout row align-end class="mb-2">
      <v-icon color="primary" style="font-size: 35px;">euro</v-icon>
      <h2
        :style="`color: ${$vuetify.theme.primary}`"
        class="ml-2"
      >Partner fees configuration</h2>
    </v-layout>
    <v-layout column class="body-2 grey--text text--darken-2 mb-4" style="text-align: left;">
      <v-flex class="py-0">
        These are the fees that will be added to the service price of the partner:
      </v-flex>
      <v-flex class="py-0 pl-4">
        - Monthly fees are fees for monthly services, like server rental or fee per vending machine
      </v-flex>
      <v-flex class="py-0 pl-4">
        - One time fees are fees that happend once, like a promotion campaign or photo shoot
      </v-flex>
    </v-layout>
    <v-layout row justify-space-between>
      <v-flex xs3>
        <v-select
          v-model="selectedPartner"
          :items="partnerObjs"
          item-text="Name"
          item-value="PartnerId"
          label="Partner"
          return-object
          :disabled="!isAdmin"
          @change="getPartnerFees"
        ></v-select>
      </v-flex>
      <v-btn fab color="secondary" @click="onAddPartnerFee">
        <v-icon>add</v-icon>
      </v-btn>
    </v-layout>
    <v-layout row>
      <v-flex xs12>
        <v-data-table
          :headers="headers"
          :items="partnerFees"
          item-key="Key"
          :custom-sort="customSort"
        >
          <template slot="items" slot-scope="props">
            <tr class="center_table">
              <td>{{ props.item.Key }}</td>
              <td>{{ formatter.format(props.item.AmountExVat) }}</td>
              <td>{{ props.item.StartDate }}</td>
              <td>{{ props.item.EndDate }}</td>
              <td>{{ getPartnerFeeDescription(props.item) }}</td>
              <td>
                <v-layout row justify-center>
                  <v-btn icon small @click="onEditPartnerFee(props.item)">
                    <v-icon small color="primary">edit</v-icon>
                  </v-btn>
                  <v-btn icon small @click="onDeletePartnerFee(props.item)">
                    <v-icon small color="primary">delete</v-icon>
                  </v-btn>
                </v-layout>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!-- Dialog to edit a partner fee -->
    <v-dialog v-model="editMode" max-width="800px" persistent style="z-index: 1000;" scrollable>
      <PartnerFeeEdit
        v-if="editMode"
        v-model="selectedPartnerFee"
        :partnerDetails="selectedPartner"
        @cancel="onCancelPartnerFeeEdit"
        @save="onSavePartnerFeeEdit"
      />
    </v-dialog>

    <!-- Confirmation dialog for deleting a partner fee -->
    <v-dialog v-model="showDeletionDialog" max-width="500px">
      <v-card>
        <v-card-title class="subheading">
          {{ $translate('DELETE_PARTNER_FEE_CONFIRM_TITLE') }}
        </v-card-title>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="secondary"
            class="text-none"
            @click="onCancelPartnerFeeDeletion"
          > {{ $translate('NO') }} </v-btn>
          <v-btn
            color="primary"
            class="text-none"
            :loading="deletionConfirmBtnLoading"
            @click="onConfirmPartnerFeeDeletion"
          > {{ $translate('YES') }} </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import * as dssRequest from '@/api/dssRequest';
import { dateTimeCompare } from '@/shared/utility';
import PartnerFeeEdit from './Financial_PartnerFeeEdit.vue';

export default {
  name: 'Financial_PartnerFees',
  components: {
    PartnerFeeEdit,
  },
  computed: {
    isAdmin() {
      return this.$store.getters['auth/userLevel'] >= 9;
    },
    accountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },
  data() {
    return {
      partnerObjs: [],
      selectedPartner: {},
      partnerFees: [],
      selectedPartnerFee: {},
      headers: [
        {
          text: 'Id',
          value: 'Key',
        },
        {
          text: 'Amount excl VAT',
          value: 'AmountExVat',
        },
        {
          text: 'Start date',
          value: 'StartDate',
          sort: (a, b) => dateTimeCompare(a, b), // Format of dateTime: dd-mm-yyyy hh:mm
        },
        {
          text: 'End date',
          value: 'EndDate',
          sort: (a, b) => dateTimeCompare(a, b), // Format of dateTime: dd-mm-yyyy hh:mm
        },
        {
          text: 'Description',
          value: 'Description',
          sortable: false,
        },
        {
          text: 'Actions',
          value: '',
          sortable: false,
        },
      ],
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      editMode: false,
      showDeletionDialog: false,
      deletionConfirmBtnLoading: false,
    };
  },
  mounted() {
    this.getPartners();
  },
  methods: {
    getPartnerFeeDescription(partnerFee) {
      if (partnerFee.Description.length > 50) {
        return `${partnerFee.Description.substr(0, 50)}...`;
      }
      return partnerFee.Description;
    },
    customSort(fees, colName, isDescending) {
      console.log('colName, isDescending : ', colName, isDescending);
      fees.sort((a, b) => {
        // For dates, sort items by unix time
        if (colName === 'StartDate' || colName === 'EndDate') {
          const dateTimeA = a[colName];
          const dateTimeB = b[colName];
          if (!isDescending) {
            return dateTimeCompare(dateTimeA, dateTimeB);
          }
          return dateTimeCompare(dateTimeB, dateTimeA);
        }

        // For string columns, sort items should be case-insensitive
        if (typeof a[colName] === 'string' && typeof b[colName] === 'string') {
          if (!isDescending) {
            return a[colName].toUpperCase() < b[colName].toUpperCase() ? -1 : 1;
          }
          return b[colName].toUpperCase() < a[colName].toUpperCase() ? -1 : 1;
        }

        // For others, just sort by raw value
        if (!isDescending) {
          return a[colName] < b[colName] ? -1 : 1;
        }
        return b[colName] < a[colName] ? -1 : 1;
      });

      return fees;
    },
    getPartners() {
      const payload = {
        FunctionId: 'AccountFunctionLibrary::getPartners',
      };

      dssRequest.doRequest(
        (response) => {
          console.log('the call for getPartners was successful', response);
          if (response.data.PartnerList != null) {
            this.partnerObjs = [...response.data.PartnerList];
          } else {
            this.partnerObjs = [];
          }

          // Sort partners by Name
          this.partnerObjs.sort((a, b) => {
            const nameA = a.Name.toUpperCase();
            const nameB = b.Name.toUpperCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
          });

          if (this.partnerObjs.length > 0) {
            if (!this.isAdmin) {
              const currentPartner = this.partnerObjs.find(
                (partner) => partner.PartnerId === this.$store.getters['auth/partnerId'],
              );

              if (currentPartner != null) {
                this.selectedPartner = { ...currentPartner };
              } else {
                this.selectedPartner = {};
              }
            } else {
              this.selectedPartner = { ...this.partnerObjs[0] };
            }
          } else {
            this.selectedPartner = {};
          }

          this.getPartnerFees();
        },
        (error) => {
          console.log('the call for getPartners was not successful', error);
          this.partnerObjs = [];
        },
        'communicateWithDBLibrary',
        this.accountId,
        payload,
      );
    },
    getPartnerFees() {
      if (this.selectedPartner.PartnerId == null) {
        this.partnerFees = [];
        return;
      }

      const payload = {
        PartnerId: this.selectedPartner.PartnerId,
      };

      dssRequest.doRequest(
        (response) => {
          console.log('the call for getPartnerFees was successful', response);
          if (response.data.PartnerFees != null) {
            this.partnerFees = [...response.data.PartnerFees];
          } else {
            this.partnerFees = [];
          }
        },
        (error) => {
          console.log('the call for getPartnerFees was not successful', error);
          this.partnerFees = [];
        },
        'getPartnerFees',
        'LocationsNode_FMM',
        payload,
      );
    },
    deletePartnerFee() {
      const payload = {
        Key: this.selectedPartnerFee.Key,
      };

      dssRequest.doRequest(
        (response) => {
          console.log('the call for deletePartnerFee was successful', response);
          this.getPartnerFees();
          this.showDeletionDialog = false;
          this.deletionConfirmBtnLoading = false;
          this.selectedPartnerFee = {};
        },
        (error) => {
          console.log('the call for deletePartnerFee was not successful', error);
          this.deletionConfirmBtnLoading = false;
        },
        'deletePartnerFee',
        'LocationsNode_FMM',
        payload,
      );
    },
    onAddPartnerFee() {
      this.selectedPartnerFee = {};
      this.editMode = true;
    },
    onEditPartnerFee(partnerFee) {
      this.selectedPartnerFee = { ...partnerFee };
      this.editMode = true;
    },
    onDeletePartnerFee(partnerFee) {
      this.selectedPartnerFee = { ...partnerFee };
      this.showDeletionDialog = true;
    },
    onCancelPartnerFeeDeletion() {
      this.showDeletionDialog = false;
      this.selectedPartnerFee = {};
    },
    onConfirmPartnerFeeDeletion() {
      this.deletionConfirmBtnLoading = true;
      this.deletePartnerFee();
    },
    onCancelPartnerFeeEdit() {
      this.editMode = false;
      this.selectedPartnerFee = {};
    },
    onSavePartnerFeeEdit() {
      this.editMode = false;
      this.selectedPartnerFee = {};
      this.getPartnerFees();
    },
  },
};
</script>
